import { Link } from "react-router-dom";

// Shared editorial frame for static info pages. Keeps every page consistent and
// avoids repeating the header/footer scaffolding.
export default function InfoShell({ kicker, title, children, testid }) {
  return (
    <div data-testid={testid} className="pb-24">
      <section className="px-6 md:px-10 pt-16 md:pt-24 pb-10 text-center max-w-3xl mx-auto">
        <div className="text-[11px] tracking-editorial uppercase text-[#C3B1C5] mb-4">◇ {kicker}</div>
        <h1 className="font-serif-display text-[44px] md:text-[60px] leading-[1.05] tracking-tight">{title}</h1>
      </section>
      <article className="max-w-2xl mx-auto px-6 md:px-10 space-y-6 text-[14px] leading-[1.9] text-[#4A464A]/85">
        {children}
      </article>
      <div className="divider-thin w-24 mx-auto mt-16" />
      <div className="text-center mt-6 text-[12px] italic text-[#C3B1C5]">
        Questions? <Link to="/contact" className="underline">Reach out</Link> any time.
      </div>
    </div>
  );
}

const H2 = ({ children }) => <h2 className="font-serif-display text-[22px] md:text-[26px] mt-10 mb-2 text-[#4A464A]">{children}</h2>;

export function Privacy() {
  return (
    <InfoShell kicker="Legal · Singapore" title="Privacy Policy" testid="privacy-page">
      <p className="italic text-[#4A464A]/70">Last updated: February 2026</p>
      <p>ONE LOOK is a small Singapore fashion label. This policy explains what we collect, why, and your rights under Singapore's Personal Data Protection Act (PDPA).</p>

      <H2>What we collect</H2>
      <p>When you place an order, sign up for our newsletter, or get in touch, we collect your <strong>name, email, phone number and delivery address</strong>. We also collect the order details and anything you choose to tell us. That's it — we don't ask for anything we don't need.</p>

      <H2>Why we collect it</H2>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>To process your order, pack it, and deliver it to you.</li>
        <li>To send order updates (confirmation, shipping notification, exchanges).</li>
        <li>To send marketing updates <em>only</em> when you opt in — you can unsubscribe at any time.</li>
      </ul>

      <H2>Payments</H2>
      <p>All payments are processed securely by our payment provider (Stripe). <strong>We never see, touch, or store your card details.</strong></p>

      <H2>Sharing</H2>
      <p>We do not sell, rent, or trade your data to anyone. We share your name, address and phone number with our delivery partners strictly for the purpose of getting your order to you.</p>

      <H2>Your rights</H2>
      <p>You can email us any time to access the personal data we hold about you, correct anything that's wrong, or ask us to delete it entirely. We'll action your request within a reasonable time, as required by the PDPA.</p>

      <H2>Contact</H2>
      <p>Email <a href="mailto:hello.onelook.sg@gmail.com" className="underline">hello.onelook.sg@gmail.com</a> for any privacy question or request.</p>
    </InfoShell>
  );
}

export function Terms() {
  return (
    <InfoShell kicker="Legal · Singapore" title="Terms of Sale" testid="terms-page">
      <p className="italic text-[#4A464A]/70">Last updated: February 2026</p>
      <p>These terms apply to every order you place on ONE LOOK (onelooksg.com). By placing an order, you agree to them.</p>

      <H2>Pre-order window</H2>
      <p>During our soft-launch pre-order mode, orders are reserved in advance. <strong>Pre-order items ship 2 weeks after the soft-launch order window closes.</strong> The exact ship-from date is shown on the product page and in your order confirmation.</p>

      <H2>Delivery</H2>
      <p>We currently ship within <strong>Singapore only</strong>. <strong>Free delivery on orders over S$88</strong> — otherwise a flat local delivery fee is calculated at checkout.</p>

      <H2>Exchanges</H2>
      <p>Exchanges for another size or another look are accepted within <strong>7 days of delivery</strong>. Items must be <strong>unworn, unwashed and with tags on</strong>. Email us to arrange an exchange — we'll send instructions.</p>

      <H2>Prices &amp; currency</H2>
      <p>All prices are in <strong>SGD</strong> and inclusive of applicable taxes. Promo codes apply only during checkout and cannot be combined with other offers unless stated.</p>

      <H2>Cancellation</H2>
      <p>Pre-orders can be cancelled any time before the pre-order window closes — just email us and we'll refund in full.</p>

      <H2>Contact</H2>
      <p>Email <a href="mailto:hello.onelook.sg@gmail.com" className="underline">hello.onelook.sg@gmail.com</a> for anything to do with your order.</p>
    </InfoShell>
  );
}

export function FAQ() {
  const qs = [
    ["What makes ONE LOOK different?", "Every look on the site is a complete, pre-matched outfit. You pick the look, we send the whole thing — no decision fatigue, no mixing and matching."],
    ["How does the pre-order work?", "During our soft launch, every order is a pre-order. Items ship 2 weeks after the pre-order window closes — the exact dates are shown on each product page and in your confirmation email."],
    ["Where do you ship?", "Singapore only, for now. Free delivery on orders over S$88."],
    ["How long does delivery take?", "After we ship your order, local delivery usually arrives within 2–4 business days."],
    ["Can I exchange or return?", "Exchanges for another size or another look are accepted within 7 days of delivery. Items must be unworn, unwashed and with tags on."],
    ["Which payment methods do you accept?", "Card and Singapore PayNow via Stripe. We never see or store your card details."],
    ["How do I know my size?", "Check the Size Guide — our body-measurement chart and figure diagram will help. Still unsure? DM us on Instagram, we're happy to help ♡"],
    ["How do I reach you?", "Email hello.onelook.sg@gmail.com or DM us on Instagram @onelook.sg."],
  ];
  return (
    <InfoShell kicker="Help" title="FAQ" testid="faq-page">
      {qs.map(([q, a], i) => (
        <div key={i} className="border-b pb-5" style={{ borderColor: "#EBE5ED" }} data-testid={`faq-item-${i}`}>
          <div className="font-serif-display text-[18px] mb-2">{q}</div>
          <div>{a}</div>
        </div>
      ))}
    </InfoShell>
  );
}

export function Shipping() {
  return (
    <InfoShell kicker="Help" title="Shipping" testid="shipping-page">
      <H2>Where we ship</H2>
      <p>We currently ship within <strong>Singapore only</strong>. International shipping is not available at this time.</p>

      <H2>Delivery fee</H2>
      <p><strong>Free local delivery</strong> on orders over S$88. For orders below that, a flat local delivery fee is calculated at checkout.</p>

      <H2>Delivery time</H2>
      <p>During our soft launch, orders are shipped <strong>2 weeks after the pre-order window closes</strong>. Once shipped, local delivery usually lands with you within 2–4 business days. You'll get an email the moment your parcel leaves our studio.</p>

      <H2>Address accuracy</H2>
      <p>Please double-check your delivery address at checkout — Singapore postal codes must be 6 digits. We're not responsible for parcels sent to incorrect addresses provided at checkout.</p>
    </InfoShell>
  );
}

export function Returns() {
  return (
    <InfoShell kicker="Help" title="Exchanges &amp; Returns" testid="returns-page">
      <H2>Exchanges</H2>
      <p>We accept exchanges for another <strong>size</strong> or another <strong>look</strong> within <strong>7 days of delivery</strong>.</p>

      <H2>Condition</H2>
      <p>Items must be <strong>unworn, unwashed and with tags on</strong>. We can't accept items that show signs of wear, wash, perfume or alterations.</p>

      <H2>How to request</H2>
      <p>Email <a href="mailto:hello.onelook.sg@gmail.com" className="underline">hello.onelook.sg@gmail.com</a> within 7 days of receiving your order, letting us know your order number and what you'd like to exchange for. We'll reply within a day or two with simple return instructions.</p>

      <H2>Sale items</H2>
      <p>Items bought with a storewide promo code are still eligible for exchange within the same window.</p>
    </InfoShell>
  );
}
